import { useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { errorMessage, getCurrentUserInfoQueryKey, schemas, updateUserSettingsMutation } from "@/api";
import { useAppForm } from "@/components/form/form";
import { Page } from "@/components/Page";
import { PageSection } from "@/components/section/SectionBlocks";
import { SectionLoading } from "@/components/section/SectionStates";
import { parseStoredSettings } from "@/features/account/storedSettings";
import { useCurrentUser } from "@/features/account/useCurrentUser";
import { type ChatModelOption, useChatModels } from "@/features/chat/useChatModels";

// Form schema built from the GENERATED field rules (max lengths, allowed
// values), so the backend stays the single source of truth. The API marks
// every field optional+nullable (a PATCH-style update); the form holds
// concrete values, with "" meaning "not set" for the selects.
const api = schemas.zUserSettingsUpdate.shape;
const notSet = z.literal("");
const settingsFormSchema = z.object({
  defaultModelId: notSet.or(api.defaultModelId.unwrap().unwrap()),
  customInstructions: api.customInstructions.unwrap().unwrap(),
  responseTone: notSet.or(api.responseTone.unwrap().unwrap()),
  responseLength: notSet.or(api.responseLength.unwrap().unwrap()),
  showContextInfo: api.showContextInfo.unwrap().unwrap(),
});
type SettingsFormValues = z.infer<typeof settingsFormSchema>;

// Option values (labels are translated); validity comes from the schema above.
const TONES = ["professional", "casual", "friendly", "concise", "direct"] as const;
const LENGTHS = ["brief", "moderate", "detailed"] as const;

const EMPTY_VALUES: SettingsFormValues = {
  defaultModelId: "",
  customInstructions: "",
  responseTone: "",
  responseLength: "",
  showContextInfo: false,
};

function toFormValues(settings: Record<string, unknown>, knownModelIds: string[] | null): SettingsFormValues {
  // `user.settings` is stored/returned in snake_case even though the update
  // body is camelCase; anything invalid falls back to defaults. A saved model
  // that's no longer offered (disabled since) counts as "not set".
  const s = parseStoredSettings(settings);
  const modelId = s.default_model_id ?? "";
  const parsed = settingsFormSchema.safeParse({
    defaultModelId: knownModelIds && !knownModelIds.includes(modelId) ? "" : modelId,
    customInstructions: s.custom_instructions ?? "",
    responseTone: s.response_tone ?? "",
    responseLength: s.response_length ?? "",
    showContextInfo: s.show_context_info ?? false,
  });
  return parsed.success ? parsed.data : EMPTY_VALUES;
}

/**
 * Reference implementation of the form pattern: generated query + mutation
 * options, the generated zod schema as validator, design-system fields via
 * useAppForm, cache invalidation, and toast feedback.
 */
export function AiPreferencesPage() {
  const me = useCurrentUser();
  const models = useChatModels();
  const { t } = useTranslation("settings");
  return (
    <Page title={t("ai.title")} subtitle={t("ai.subtitle")}>
      {/* key: remount the form with fresh defaults once the profile arrives. */}
      {me.data && !models.isLoading ? (
        <AiPreferencesForm
          key={me.dataUpdatedAt}
          settings={me.data.user.settings ?? {}}
          modelOptions={models.options}
          modelsLoaded={models.isLoaded}
        />
      ) : (
        <SectionLoading />
      )}
    </Page>
  );
}

function AiPreferencesForm({
  settings,
  modelOptions,
  modelsLoaded,
}: Readonly<{ settings: Record<string, unknown>; modelOptions: ChatModelOption[]; modelsLoaded: boolean }>) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation("settings");
  const defaultOption = { value: "", label: t("ai.default") };
  const toneOptions = [defaultOption, ...TONES.map((v) => ({ value: v, label: t(`ai.tones.${v}`) }))];
  const lengthOptions = [defaultOption, ...LENGTHS.map((v) => ({ value: v, label: t(`ai.lengths.${v}`) }))];
  const modelSelectOptions = [
    { value: "", label: t("ai.organizationDefault") },
    ...modelOptions.map((m) => ({ value: m.id, label: m.label })),
  ];
  const save = useMutation({
    ...updateUserSettingsMutation(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getCurrentUserInfoQueryKey() }),
  });

  const form = useAppForm({
    defaultValues: toFormValues(settings, modelsLoaded ? modelOptions.map((m) => m.id) : null),
    validators: { onChange: settingsFormSchema },
    onSubmit: async ({ value }) => {
      try {
        await save.mutateAsync({
          body: {
            ...value,
            // "" means "not set": send null so the backend clears it.
            defaultModelId: value.defaultModelId || null,
            responseTone: value.responseTone || null,
            responseLength: value.responseLength || null,
          },
        });
        toast.success(t("saved"));
      } catch (error) {
        toast.error(errorMessage(error, t("saveFailed")));
      }
    },
  });

  return (
    <form
      aria-label={t("ai.label")}
      className="flex flex-col gap-8"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <PageSection title={t("ai.modelSection")} description={t("ai.modelDescription")}>
        <form.AppField name="defaultModelId">
          {(field) => (
            <field.SelectField
              label={t("ai.defaultModel")}
              placeholder={t("ai.organizationDefault")}
              options={modelSelectOptions}
            />
          )}
        </form.AppField>
      </PageSection>
      <PageSection title={t("ai.styleSection")} description={t("ai.styleDescription")}>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <form.AppField name="responseTone">
            {(field) => <field.SelectField label={t("ai.tone")} placeholder={t("ai.default")} options={toneOptions} />}
          </form.AppField>
          <form.AppField name="responseLength">
            {(field) => (
              <field.SelectField label={t("ai.length")} placeholder={t("ai.default")} options={lengthOptions} />
            )}
          </form.AppField>
        </div>
        <form.AppField name="customInstructions">
          {(field) => (
            <field.TextareaField
              label={t("ai.customInstructions")}
              placeholder={t("ai.customInstructionsPlaceholder")}
            />
          )}
        </form.AppField>
      </PageSection>
      <PageSection title={t("ai.chatSection")} description={t("ai.contextDescription")}>
        <form.AppField name="showContextInfo">
          {(field) => <field.ToggleField label={t("ai.showContextInfo")} />}
        </form.AppField>
      </PageSection>
      <form.AppForm>
        <form.SubmitActions onCancel={() => form.reset()} />
      </form.AppForm>
    </form>
  );
}
